<template>
  <div>
    <h3 class="subtitle1 text-primary mb-4" data-testid="block-auto-forwarding-title">
      {{ $t("emailSettings.blockAutoForwarding.title") }}
    </h3>
    <v-card class="pa-6">
      <div class="d-flex align-center">
        <v-checkbox
          v-model="enabledModel"
          :disabled="disabled"
          :ripple="false"
          color="primary"
          data-testid="enable-auto-forwarding-checkbox"
        >
          <template #label>
            <div
              class="subtitle1"
              :class="{ 'text-indigo-medium': disabled }"
              v-text="$t('emailSettings.blockAutoForwarding.enableToggle')"
            />
          </template>
        </v-checkbox>
      </div>

      <template v-if="localValue.autoForwardingBlockingPolicy.enabled && !isGlobalPolicy">
        <email-report-config-section
          v-model="localValue.autoForwardingBlockingPolicy"
          :disabled="disabled"
          :context="EmailReportConfigContext.AUTO_FORWARDING_BLOCKING_POLICY"
        />
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, toRefs, computed } from "vue";
import type { PropType } from "vue";
import type {
  BlockedEmailReportUser,
  EmailSecuritySettings,
} from "@/_store/email-security/email-settings.module";
import { isCloudAppConnected } from "@/_helpers/cloud-security.ts";
import EmailReportConfigSection from "@/components/EmailReportConfigSection.vue";
import { EmailReportConfigContext, EmailSettingsAction } from "@/constants/emails.ts";
import { ModalWidth } from "@/constants/modals.ts";
import AddAdminEmailAddressModal from "@/components/modals/email-settings/AddAdminEmailAddressModal.vue";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps.ts";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  components: { EmailReportConfigSection },
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isGlobalPolicy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const dialogsStore = useDialogsStore();
    const i18n = useI18n();
    const router = useRouter();

    const localValue = useTwoWayBinding(modelValue, (value: EmailSecuritySettings) =>
      emit("update:modelValue", value)
    );

    const hasEmailCloudServiceConnected = computed(() => {
      return isCloudAppConnected(Service.OFFICE_365) || isCloudAppConnected(Service.G_SUITE);
    });

    const navigateToCloudApps = () => {
      const routeData = router.resolve({
        name: RouteName.CLOUD_APPS_PAGE,
      });
      window.open(routeData.href, "_blank");
    };

    const onActionClick = (action: EmailSettingsAction) => {
      const { openDialog } = dialogsStore;

      switch (action) {
        case EmailSettingsAction.CONNECT_AUTO_CLOUD_APP_WARNING:
          openDialog({
            ...confirmationDialogsConfigConstructor({
              item: null,
              action,
              callback: navigateToCloudApps,
            }),
            header: {
              title: i18n.t(`modals.${action}.title`),
            },
          });
          break;
        case EmailSettingsAction.ADD_ADMIN_EMAIL_ADDRESS:
        case EmailSettingsAction.EDIT_ADMIN_EMAIL_ADDRESS: {
          const isEditMode = action === EmailSettingsAction.EDIT_ADMIN_EMAIL_ADDRESS;
          openDialog({
            ...componentDialogsConfigConstructor({
              callback: (user: BlockedEmailReportUser) => {
                localValue.value.adminEmail = user.email;
                localValue.value.autoForwardingBlockingPolicy.enabled = true;
              },
              width: ModalWidth.MEDIUM,
              component: AddAdminEmailAddressModal,
              action,
              item: isEditMode
                ? ({
                    email: localValue.value.adminEmail,
                    userId: "",
                  } as BlockedEmailReportUser)
                : null,
            }),
            header: {
              title: i18n.t(`modals.${action}.title`),
            },
          });
          break;
        }
      }
    };

    const enabledModel = computed({
      get: () => localValue.value.autoForwardingBlockingPolicy.enabled,
      set: (value: boolean) => {
        if (value) {
          if (!hasEmailCloudServiceConnected.value && !props.isGlobalPolicy) {
            onActionClick(EmailSettingsAction.CONNECT_AUTO_CLOUD_APP_WARNING);
            return;
          }
          if (!localValue.value.adminEmail) {
            onActionClick(EmailSettingsAction.ADD_ADMIN_EMAIL_ADDRESS);
            return;
          }
        }
        localValue.value.autoForwardingBlockingPolicy.enabled = value;
      },
    });

    return {
      localValue,
      enabledModel,
      EmailReportConfigContext,
    };
  },
});
</script>
